import { FormEvent, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CurrentsAPI, type OceanSkillType } from "@vida/currents-core";
import { createCurrentsApiConfig } from "../services/currentsSession";
import {
  CREATABLE_SKILL_TYPES,
  DEFAULT_MODEL_BINDING_JSON,
  type CreatableSkillType,
} from "../services/agentCatalog";

export function SkillNewPage() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [skillType, setSkillType] = useState<CreatableSkillType>("SCRIPT");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selected = CREATABLE_SKILL_TYPES.find((t) => t.value === skillType)!;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const cfg = createCurrentsApiConfig();
      const skill = await CurrentsAPI.createOrgSkill(cfg, {
        name: name.trim(),
        description: description.trim() || undefined,
        skillType: skillType as OceanSkillType,
        icon: selected.icon,
        modelBindingJson: skillType === "MODEL" ? DEFAULT_MODEL_BINDING_JSON : undefined,
      });
      if (skillType === "SCRIPT" && description.trim()) {
        await CurrentsAPI.generateSkillScript(cfg, skill.id, description.trim());
      }
      navigate(`/skills/${skill.id}/edit`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <p className="muted" style={{ margin: 0 }}>
            <Link to="/skills">← Skills</Link>
          </p>
          <h1>Nuova skill</h1>
          <p className="muted">Scegli il tipo, poi configura script o binding modello nell’editor.</p>
        </div>
      </header>

      {error && <p className="err">{error}</p>}

      <form className="card" onSubmit={(e) => void onSubmit(e)}>
        <fieldset className="skill-type-fieldset">
          <legend>Tipo skill</legend>
          <div className="skill-type-grid">
            {CREATABLE_SKILL_TYPES.map((option) => (
              <label
                key={option.value}
                className={`skill-type-option${skillType === option.value ? " selected" : ""}`}
              >
                <input
                  type="radio"
                  name="skillType"
                  value={option.value}
                  checked={skillType === option.value}
                  onChange={() => setSkillType(option.value)}
                />
                <span className="skill-type-option-icon" aria-hidden>
                  {option.icon}
                </span>
                <span className="skill-type-option-label">{option.label}</span>
                <span className="skill-type-option-desc muted">{option.description}</span>
              </label>
            ))}
          </div>
          <p className="muted" style={{ marginBottom: 0 }}>
            Le skill <code>TOOL_BUNDLE</code> e <code>PROMPT</code> restano nel catalogo seed org (non creabili
            manualmente).
          </p>
        </fieldset>

        <label className="field">
          <span>Nome</span>
          <input value={name} onChange={(e) => setName(e.target.value)} required maxLength={80} />
        </label>
        <label className="field">
          <span>{skillType === "SCRIPT" ? "Descrizione / prompt generazione script" : "Descrizione"}</span>
          <textarea
            rows={5}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={
              skillType === "SCRIPT"
                ? "Es. digest settimanale articoli Reef filtrati per topic dall'input"
                : "Es. generazione immagini prodotto con parametri subject, style, background"
            }
            required={skillType === "SCRIPT"}
          />
        </label>
        <button
          type="submit"
          className="btn primary"
          disabled={busy || !name.trim() || (skillType === "SCRIPT" && !description.trim())}
        >
          {busy
            ? "Creazione…"
            : skillType === "SCRIPT"
              ? "Crea e genera script"
              : "Crea skill MODEL"}
        </button>
      </form>
    </div>
  );
}
